// Any hero's sheet, for anyone at the table: click a hero in a lobby seat or on the writing screen
// and see who they are — class, species, background, loadout, calling, the numbers the sim gives
// them, every ability they carry, their level cards, what they wear and carry, and their record.
// Read-only (my own hero's inventory is the docs/71 inventory, opened from the World screen). `openHeroSheet`
// (heroSheetStore.ts) from anywhere; `HeroSheetHost` is mounted once in App. Everything shown is public World data
// (hero, hero_origin, hero_calling, item) and the sim's own preview and catalogues.
import { ItemIcon, itemTooltip } from '../inventory/ItemCard';
import { Fragment, useMemo } from 'react';
import { Badge, Button, HeroSprite, Modal, Stat, Stats } from '../components';
import { useOverlay } from '../overlay';
import { useHeroCallings, useHeroOrigins, useHeroProgress, useItemUpgrades, useNames, useWorldHeroes, useWorldItems } from '../../core/subscriptions/hooks';
import { useSession } from '../../core/stores/session';
import { identityHex } from '../../core/net/connection';
import { getBridge } from '../../core/encounter/session';
import { wasmModule } from '../../core/sim/wasm';
import { CLASS_NAMES, SPECIES_NAMES } from '../../core/sim/adapt';
import type { HeroPreview } from '../../core/sim/WasmSimBridge';
import type { WasmAbility } from '../../core/sim/actions';
import { AbilityCard } from '../screens/forge/Details';
import { abilityDoc, buildWord, universalAbilities, callingsFor, cap, classFeatures, classResource, CALLING_KIND, itemLines } from '../screens/forge';
import { describeAbility } from '../../core/sim/describe';
import { gradeWord, slotLabel, upgradeSegs } from '../../core/sim/gear';
import { itemDetails } from '../../core/sim/items';
import { LEVEL_CARDS } from '../screens/levelupData';
import { useHeroSheet } from './heroSheetStore';
import '../screens/forge.css';
import './heroes.css';

export function HeroSheetHost() {
  const heroId = useHeroSheet((s) => s.heroId);
  const close = useHeroSheet((s) => s.close);
  const worldId = useSession((s) => s.worldId);
  const hero = useWorldHeroes(worldId).find((h) => h.id === heroId);
  const classId = hero ? CLASS_NAMES[hero.class] ?? 'fighter' : 'fighter';
  return (
    <Modal open={!!hero} onClose={close} eyebrow={hero ? `Level ${hero.level} ${cap(classId)}` : undefined} title={hero?.name} size="narrow" testId="hero-inspect" className="fk-forge__details fk-inspect">
      {hero && worldId !== undefined && <SheetBody worldId={worldId} heroId={hero.id} />}
    </Modal>
  );
}

function SheetBody({ worldId, heroId }: { worldId: bigint; heroId: bigint }) {
  const h = useWorldHeroes(worldId).find((x) => x.id === heroId)!;
  const origin = useHeroOrigins(worldId).find((o) => o.heroId === heroId);
  const calling = useHeroCallings(worldId).find((k) => k.heroId === heroId);
  const progress = useHeroProgress(worldId).find((r) => r.heroId === heroId);
  const items = useWorldItems(worldId).filter((i) => i.heroId === heroId).sort((a, b) => (a.equippedSlot ?? 99) - (b.equippedSlot ?? 99) || (a.id < b.id ? -1 : 1));
  // docs/112: what is laid on each piece, for its icon, its tooltip and the numbers
  const ups = useItemUpgrades(worldId);
  const upsOf = (id: bigint): number[] => [...(ups.find((u) => u.itemId === id)?.applied ?? [])];
  const names = useNames(worldId);
  const me = useSession((s) => s.identity ?? '');
  const bridge = getBridge();
  const catalogue = useMemo(() => { try { return wasmModule().abilities() as WasmAbility[]; } catch { return []; } }, []);
  const classId = CLASS_NAMES[h.class] ?? 'fighter';
  const resource = classResource(h.class);
  const preview: HeroPreview | undefined = useMemo(() => {
    if (!bridge || !origin) return undefined;
    try { return bridge.previewHero(h.class, h.species, h.build, origin.background, origin.loadout, h.level, calling?.pick ?? 0, calling?.spells ?? []); } catch { return undefined; }
  }, [bridge, origin, calling, h.class, h.species, h.build, h.level]);
  const origins = bridge?.origins();
  const callings = bridge?.callings();
  const background = origins?.backgrounds.find((b) => b.id === origin?.background);
  const loadout = origins?.loadouts.find((l) => l.class === h.class && l.id === origin?.loadout);
  const kind = CALLING_KIND[h.class];
  const callingDef = callings && calling ? callingsFor(callings, h.class).find((d) => d.id === calling.pick) : undefined;
  const spellNames = kind === 'spells' ? (calling?.spells ?? []).map((id) => catalogue.find((a) => a.id === id)?.name).filter(Boolean) : [];
  // docs/71 INV8: the numbers from what the hero wears now, with its level cards, as the fight loads them
  const wornItems = items.filter((i) => i.equippedSlot !== undefined && i.equippedSlot !== null);
  const worn = wornItems.flatMap((i) => [i.equippedSlot!, i.def, i.tier]);
  const wornUps = upgradeSegs(wornItems.map((i) => ({ upgrades: upsOf(i.id) })));
  const gearStats = useMemo(() => {
    try { return bridge?.previewHeroGear(h.class, h.species, h.build, h.level, calling?.pick ?? 0, calling?.spells ?? [], h.cards, worn, wornUps, progress); } catch { return undefined; }
  }, [bridge, h.class, h.species, h.build, h.level, calling, progress, h.cards, worn.join(','), wornUps.join(',')]);
  const s = gearStats ?? preview?.stats;
  // the kit preview has no level cards: Hardy (1) and Fleet (5) as `derive_stats` adds them (docs/58 FO3)
  const count = (c: number) => (gearStats ? 0 : h.cards.filter((x) => x === c).length);
  // what sets this hero apart (class, calling, species) as full cards; what every hero can do in a line
  // docs/103: what the class always has (the rogue's Backstab), named up front
  const features = classFeatures(catalogue, classId).filter((f) => (s?.abilities ?? []).includes(f.id));
  const basics = universalAbilities(catalogue, s?.abilities ?? []);
  const abilities = (s?.abilities ?? []).map((id) => catalogue.find((a) => a.id === id)).filter((a): a is WasmAbility => !!a && !basics.includes(a)).map((a) => abilityDoc(a, resource));
  const kitLines = (name: string, def: number, tier: number) => {
    const card = bridge?.itemCard(def, tier);
    if (card) return itemDetails(card).effects;
    const k = preview?.kit.find((x) => x.name === name);
    return k ? itemLines(k, resource) : [];
  };
  const owner = identityHex(h.owner);
  const ownerName = owner === me ? 'you' : names[owner] ?? 'another player';
  const species = cap(SPECIES_NAMES[h.species] ?? `species ${h.species}`);

  return (
    <div className="fk-forge__dbody" data-testid="hero-inspect-body" data-hero-id={h.id.toString()}>
      <div className="fk-forge__dlead">
        <span className="fk-forge__dportrait"><HeroSprite heroId={h.id} species={h.species} frame={h.oryxFrame} classId={classId} scale={3} title={h.name} /></span>
        <div className="fk-inspect__id">
          <div className="fk-inspect__line">{cap(classId)} · {species} · {buildWord(h.build)}</div>
          <div className="fk-inspect__line fk-inspect__line--faint">Played by {ownerName}</div>
          {calling?.petName && <div className="fk-inspect__line">Companion: {calling.petName}</div>}
        </div>
      </div>

      {s && (
        <Stats className="fk-forge__dstats" aria-label="Numbers">
          <Stat label="HP" value={Math.min(90, s.hp_max + 3 * count(1))} />
          <Stat label="Move" value={Math.min(6, s.move_tiles + count(5))} />
          <Stat label="Guard" value={s.guard + count(2)} />
          <Stat label="Power" value={s.power + count(3)} />
          <Stat label="Focus" value={s.focus + count(4)} />
          {s.resource.max > 0 && <Stat label={resource} value={s.resource.max} />}
        </Stats>
      )}

      <dl className="fk-inspect__origin">
        {background && <><dt>Background</dt><dd><b>{background.name}</b>{background.blurb ? ` — ${background.blurb}` : ''}</dd></>}
        {loadout && <><dt>Loadout</dt><dd><b>{loadout.name}</b>{loadout.blurb ? ` — ${loadout.blurb}` : ''}</dd></>}
        {callingDef && <><dt>{kind === 'companion' ? 'Companion' : cap(callingDef.kind)}</dt><dd><b>{callingDef.name}{callingDef.title ? `, ${callingDef.title}` : ''}</b>{callingDef.blurb ? ` — ${callingDef.blurb}` : ''}</dd></>}
        {features.map((f) => <Fragment key={f.id}><dt>Class ability</dt><dd data-testid="hero-inspect-class-feature"><b>★ {f.name}</b> — {describeAbility(f)}</dd></Fragment>)}
        {spellNames.length > 0 && <><dt>Spells</dt><dd>{spellNames.join(' · ')}</dd></>}
        {basics.length > 0 && <><dt>Every hero</dt><dd>{basics.map((a) => a.name).join(' · ')}</dd></>}
        {h.cards.length > 0 && <><dt>Level cards</dt><dd>{h.cards.map((c) => LEVEL_CARDS[c]?.title ?? `card ${c}`).join(' · ')}</dd></>}
      </dl>

      {abilities.length > 0 && <ul className="fk-forge__dabilities" aria-label="Abilities" data-testid="hero-inspect-abilities">{abilities.map((a) => <AbilityCard key={a.id} a={a} />)}</ul>}

      {items.length > 0 && (
        <ul className="fk-forge__ditems" aria-label="Gear" data-testid="hero-inspect-gear">
          {items.map((i) => {
            const lines = kitLines(i.name, i.def, i.tier);
            return (
              <li key={i.id.toString()} className="fk-forge__ditem">
                <ItemIcon def={i.def} tier={i.tier} name={i.name} relic={i.relic} scale={2} framed upgrades={upsOf(i.id)} title={itemTooltip(i.def, i.tier, i.name, i.relic, upsOf(i.id))} />
                <div className="fk-forge__ditem-body">
                  <div className="fk-forge__ditem-head"><span className="fk-forge__ditem-name">{i.name}{i.relic ? <> <Badge tone="plum">relic</Badge></> : null}</span><span className="fk-forge__ditem-slot">{i.equippedSlot !== undefined && i.equippedSlot !== null ? slotLabel(i.equippedSlot) : 'In the pack'}</span></div>
                  <div className="fk-forge__ditem-family">{gradeWord(i.def, i.tier, i.relic)}</div>
                  {lines.length > 0 && <div className="fk-forge__ditem-lines">{lines.map((l) => <span key={l}>{l}</span>)}</div>}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {h.level > 1 && (
        <div className="fk-row" data-testid="hero-inspect-path">
          <Button size="sm" onClick={() => useOverlay.getState().openHeroPath(h.id)}>{owner === me ? 'Levels, training and talents' : 'Levels and path'}</Button>
        </div>
      )}

      <ul className="fk-forge__dnotes" aria-label="Record">
        <li>{h.fights} fight{h.fights === 1 ? '' : 's'} · {h.victories} victor{h.victories === 1 ? 'y' : 'ies'} · {h.finalBlows} final blow{h.finalBlows === 1 ? '' : 's'} · downed {h.timesDowned} time{h.timesDowned === 1 ? '' : 's'}</li>
      </ul>
    </div>
  );
}
